import {
  Briefcase,
  MapPin,
  Shield,
  SlidersHorizontal,
  Target,
  type LucideIcon,
} from 'lucide-react';
import {
  FILTER_QUESTIONS,
  YES_NO_OPTIONS,
  type FilterQuestion,
} from '@nemo/core/talent/questionnaire';
import { FilterDialogFrame, FilterGroup, FilterRow, type FilterChip } from '../ui/FilterDialog.js';
import { Select } from '../ui/Select.js';

/** Every filter the Talent page can hold; the typed ones are trimmed where the query is built. */
export interface TalentFilters {
  readonly location: string;
  readonly skill: string;
  readonly school: string;
  readonly language: string;
  readonly minYears: string;
  readonly minYearsServed: string;
  readonly answers: Readonly<Record<string, string>>;
}

export const NO_TALENT_FILTERS: TalentFilters = {
  location: '',
  skill: '',
  school: '',
  language: '',
  minYears: '',
  minYearsServed: '',
  answers: {},
};

const MIN_YEARS_OPTIONS = [
  { value: '', label: 'Any experience' },
  { value: '2', label: '2+ years' },
  { value: '5', label: '5+ years' },
  { value: '10', label: '10+ years' },
];

const MIN_YEARS_SERVED_OPTIONS = [
  { value: '', label: 'Any service' },
  { value: '2', label: '2+ years served' },
  { value: '5', label: '5+ years served' },
];

type TextKey = 'location' | 'skill' | 'school' | 'language';

const TEXT_FILTERS: readonly { key: TextKey; label: string }[] = [
  { key: 'location', label: 'Location' },
  { key: 'skill', label: 'Skill' },
  { key: 'school', label: 'School' },
  { key: 'language', label: 'Language' },
];

const TEXT_LABELS = new Map(TEXT_FILTERS.map(({ key, label }) => [key, label]));

/**
 * One control in the dialog. The CV's reading and the questionnaire answer
 * the same questions, so the dialog groups by topic and never says which
 * source a filter reads.
 */
type FilterControl =
  | { kind: 'text'; key: TextKey }
  | { kind: 'question'; id: string }
  | { kind: 'minYears' }
  | { kind: 'minYearsServed' };

const FILTER_LAYOUT: readonly { title: string; icon: LucideIcon; controls: FilterControl[] }[] = [
  {
    title: 'Location and background',
    icon: MapPin,
    controls: [
      { kind: 'text', key: 'location' },
      { kind: 'question', id: 'country_from' },
      { kind: 'question', id: 'citizenships' },
      { kind: 'text', key: 'language' },
    ],
  },
  {
    title: 'Service',
    icon: Shield,
    controls: [
      { kind: 'question', id: 'branch' },
      { kind: 'question', id: 'countries_experience' },
      { kind: 'minYearsServed' },
      { kind: 'question', id: 'deployed_abroad' },
      { kind: 'question', id: 'clearance' },
    ],
  },
  {
    title: 'Looking for',
    icon: Target,
    controls: [
      { kind: 'question', id: 'positions' },
      { kind: 'question', id: 'expertise' },
    ],
  },
  {
    title: 'Experience',
    icon: Briefcase,
    controls: [
      { kind: 'minYears' },
      { kind: 'text', key: 'skill' },
      { kind: 'text', key: 'school' },
    ],
  },
];

const PLACED_QUESTIONS = new Set(
  FILTER_LAYOUT.flatMap((group) =>
    group.controls.flatMap((control) => (control.kind === 'question' ? [control.id] : [])),
  ),
);

/** A question marked as a filter after this layout was written still shows, under More. */
const UNPLACED_QUESTIONS = FILTER_QUESTIONS.filter(
  (question) => !PLACED_QUESTIONS.has(question.id),
);

const FILTER_QUESTIONS_BY_ID = new Map(FILTER_QUESTIONS.map((question) => [question.id, question]));

function questionName(question: FilterQuestion): string {
  return question.filterLabel ?? question.label;
}

function questionValues(question: FilterQuestion): readonly { value: string; label: string }[] {
  return question.type === 'yes_no' ? YES_NO_OPTIONS : question.options;
}

function withAnswer(filters: TalentFilters, id: string, value: string): TalentFilters {
  const { [id]: _dropped, ...rest } = filters.answers;
  return { ...filters, answers: value === '' ? rest : { ...rest, [id]: value } };
}

/** One removable chip per set filter, worded "Label: value". */
export function talentFilterChips(filters: TalentFilters): FilterChip<TalentFilters>[] {
  const chips: FilterChip<TalentFilters>[] = [];
  for (const { key, label } of TEXT_FILTERS) {
    const value = filters[key].trim();
    if (value !== '') {
      chips.push({ key, label: `${label}: ${value}`, remove: (f) => ({ ...f, [key]: '' }) });
    }
  }
  const minYears = MIN_YEARS_OPTIONS.find((o) => o.value === filters.minYears);
  if (filters.minYears !== '' && minYears !== undefined) {
    chips.push({
      key: 'minYears',
      label: `Experience: ${minYears.label}`,
      remove: (f) => ({ ...f, minYears: '' }),
    });
  }
  const served = MIN_YEARS_SERVED_OPTIONS.find((o) => o.value === filters.minYearsServed);
  if (filters.minYearsServed !== '' && served !== undefined) {
    chips.push({
      key: 'minYearsServed',
      label: `Years served: ${served.label.replace(' years served', '')} years`,
      remove: (f) => ({ ...f, minYearsServed: '' }),
    });
  }
  for (const question of FILTER_QUESTIONS) {
    const value = filters.answers[question.id];
    if (value === undefined) continue;
    const option = questionValues(question).find((o) => o.value === value);
    chips.push({
      key: `answer:${question.id}`,
      label: `${questionName(question)}: ${option?.label ?? value}`,
      remove: (f) => withAnswer(f, question.id, ''),
    });
  }
  return chips;
}

export function TalentFilterDialog(props: {
  filters: TalentFilters;
  matching: number;
  onChange: (filters: TalentFilters) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { filters, onChange } = props;

  function questionRow(question: FilterQuestion): React.JSX.Element {
    return (
      <FilterRow key={question.id} label={question.shortLabel}>
        <Select
          label={question.shortLabel}
          value={filters.answers[question.id] ?? ''}
          options={[{ value: '', label: 'Any' }, ...questionValues(question)]}
          onChange={(value) => onChange(withAnswer(filters, question.id, value))}
        />
      </FilterRow>
    );
  }

  function control(entry: FilterControl): React.JSX.Element | null {
    switch (entry.kind) {
      case 'text': {
        const label = TEXT_LABELS.get(entry.key) ?? entry.key;
        return (
          <FilterRow key={entry.key} label={label}>
            <input
              aria-label={label}
              placeholder={label}
              value={filters[entry.key]}
              onChange={(event) => onChange({ ...filters, [entry.key]: event.target.value })}
            />
          </FilterRow>
        );
      }
      case 'question': {
        const question = FILTER_QUESTIONS_BY_ID.get(entry.id);
        return question === undefined ? null : questionRow(question);
      }
      case 'minYears':
        return (
          <FilterRow key="minYears" label="Experience">
            <Select
              label="Minimum experience"
              value={filters.minYears}
              onChange={(minYears) => onChange({ ...filters, minYears })}
              options={MIN_YEARS_OPTIONS}
            />
          </FilterRow>
        );
      case 'minYearsServed':
        return (
          <FilterRow key="minYearsServed" label="Years served">
            <Select
              label="Minimum years served"
              value={filters.minYearsServed}
              onChange={(minYearsServed) => onChange({ ...filters, minYearsServed })}
              options={MIN_YEARS_SERVED_OPTIONS}
            />
          </FilterRow>
        );
      default:
        return unreachable(entry);
    }
  }

  return (
    <FilterDialogFrame
      canClear={talentFilterChips(filters).length > 0}
      onClear={() => onChange(NO_TALENT_FILTERS)}
      onClose={props.onClose}
      showLabel={`Show ${props.matching} candidate${props.matching === 1 ? '' : 's'}`}
    >
      {FILTER_LAYOUT.map((group) => (
        <FilterGroup key={group.title} title={group.title} icon={group.icon}>
          {group.controls.map(control)}
        </FilterGroup>
      ))}
      {UNPLACED_QUESTIONS.length > 0 && (
        <FilterGroup title="More" icon={SlidersHorizontal}>
          {UNPLACED_QUESTIONS.map(questionRow)}
        </FilterGroup>
      )}
    </FilterDialogFrame>
  );
}

function unreachable(value: never): never {
  throw new Error(`Unhandled filter: ${JSON.stringify(value)}`);
}
